import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import type { TableAddAction } from "_shared/_views/tables/table-add-actions";
import LoadingButton from "_shared/_views/loading/loading-button";
import { type ReactNode, useState } from "react";

/** What the action is called wherever a proposal tab offers it. */
export const SAVE_AS_TEMPLATE_LABEL = "Save as Template";

export type CreateTemplateActionOptions = {
  /** The naming dialog's title, e.g. "Save as Fee Schedule Template". */
  dialogTitle: string;
  /** Why the action cannot be taken yet, shown in the button's tooltip. Undefined when it can. */
  disabledReason?: string;
  /** The name the dialog opens with. */
  defaultName: string;
  /**
   * Sends the create for the name the user settled on. Resolves true once the template exists, and
   * false when it does not — the caller has already told the user why, so the dialog stays open.
   */
  onCreate: (name: string) => Promise<boolean>;
  /** More fields under the name, such as which teams a proposal template keeps (#6637). */
  options?: ReactNode;
  /** Called as the dialog opens, for a caller whose `options` start over each time. */
  onOpen?: () => void;
};

export type CreateTemplateAction = {
  /**
   * Save as Template, drawn as its own button beside the add control of the table it saves (#5808).
   * It sits with the fee schedule or the roster it copies, not with the template panel, which is where
   * templates are applied (#5394). It is not an entry in the add dropdown, where a reader had to open a
   * menu about adding to find it.
   */
  action: TableAddAction;
  /**
   * The dialog that names the template. Mounted by the tab, outside the table, so the name being
   * typed lives as long as the tab does rather than as long as the menu the action was chosen from.
   */
  dialog: ReactNode;
};

/**
 * Save as Template on a proposal tab, and the dialog that names the template it saves (#5394).
 *
 * A proposal tab that applies templates of some kind can also save what it holds as a new one. The
 * draft and active proposal pages build that roster or fee schedule differently, so the caller says
 * what to save through `onCreate`; everything the user sees is here, once.
 */
export default function useCreateTemplateAction(options: Readonly<CreateTemplateActionOptions>): CreateTemplateAction {
  const { dialogTitle, disabledReason, defaultName, onCreate, onOpen } = options;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const trimmedName = templateName.trim();

  function handleActionChosen() {
    setTemplateName(defaultName);
    onOpen?.();
    setIsDialogOpen(true);
  }

  function handleDialogClosed() {
    if (isCreating) return;
    setIsDialogOpen(false);
  }

  async function handleCreateConfirmed() {
    // Enter in the name field reaches here as well as the button, and a second press while the
    // first request is out would save the template twice.
    if (!trimmedName || isCreating) return;
    setIsCreating(true);
    const created = await onCreate(trimmedName);
    setIsCreating(false);
    if (created) setIsDialogOpen(false);
  }

  const action: TableAddAction = {
    label: SAVE_AS_TEMPLATE_LABEL,
    // Not the plus the add control carries: it copies the table rather than adding to it. No colour of
    // its own, so it takes the button's, and greys out with the label when the button is disabled.
    icon: <SaveAsOutlinedIcon fontSize="small" />,
    disabled: Boolean(disabledReason) || isCreating,
    disabledReason,
    loading: isCreating,
    onClick: handleActionChosen,
  };

  const dialog = (
    <Dialog fullWidth maxWidth="xs" open={isDialogOpen} onClose={handleDialogClosed}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Template Name"
          margin="dense"
          value={templateName}
          onChange={(event) => setTemplateName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void handleCreateConfirmed();
          }}
        />
        {options.options}
      </DialogContent>
      <DialogActions>
        <Button disabled={isCreating} onClick={handleDialogClosed}>
          Cancel
        </Button>
        <LoadingButton loading={isCreating} disabled={!trimmedName} onClick={handleCreateConfirmed}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );

  return { action, dialog };
}
